/**
 * Hold slips on the device until they upload.
 *
 * The failure that matters is a dead signal in a shop, and a slip that fails
 * to upload is a slip that gets thrown away. So the photo goes into IndexedDB
 * first and the network is treated as something that might not be there.
 *
 * Downscaling happens here rather than on the server: it makes the upload
 * survivable on a bad connection, and there is no reason to move three
 * megabytes when four hundred kilobytes reads identically.
 */
const DB = 'slips', STORE = 'queue', MAX_EDGE = 1600, QUALITY = 0.8;

function open() {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'clientRef' });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/** Downscale so the long edge is at most MAX_EDGE, as JPEG. */
export async function downscale(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale), h = Math.round(bitmap.height * scale);
  const canvas = new OffscreenCanvas(w, h);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY });
  return { blob, width: w, height: h };
}

/**
 * Queue a slip against the entity it was photographed for.
 *
 * `entityId` IS NOT OPTIONAL, and it is the whole reason this record is not
 * just `{ clientRef, pages }`. The queue outlives the screen: a practice user
 * photographs a slip for client A with no signal, switches to client B, the
 * slips screen remounts and flushes — and without an entity on the record
 * that photograph is filed against client B's ledger. The server cannot catch
 * it, because a practice user genuinely holds `capture.slip` on both. One
 * client's money in another client's books is about the worst silent outcome
 * this system has, so the entity travels WITH the photograph.
 */
export async function enqueueSlip({ entityId, pages }) {
  // crypto.randomUUID is the idempotency key the server dedupes on, and it is
  // minted ONCE here, for the whole slip — not once per page. A per-page key
  // would defeat the server-side deduplication the whole queue rests on.
  const clientRef = crypto.randomUUID();
  const db = await open();
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ clientRef, entityId, pages, queuedAt: Date.now() });
    tx.oncomplete = res; tx.onerror = () => rej(tx.error);
  });
  return clientRef;
}

/**
 * Read a queue record as `{ clientRef, entityId, pages, queuedAt, ... }`,
 * whichever shape it was written in.
 *
 * A phone can hold items captured before this multi-page bundle shipped —
 * the old shape was a single `{ clientRef, blob, width, height, queuedAt }`
 * with no `pages` array at all. If those are not read back correctly the
 * photograph they hold is lost, because nothing else can reconstruct it.
 * `flushQueue` maps every item through this before posting, so both shapes
 * end up looking identical to the rest of the module.
 *
 * A record written before entity scoping has no `entityId` either, and this
 * is the one place both shapes are reconciled, so the defaulting belongs
 * here: an item with no entity is treated as belonging to `entityId`, the
 * entity currently on screen. That keeps today's single-entity behaviour
 * exactly as it was and strands no queued photograph — the alternative,
 * refusing to upload anything unlabelled, would leave a slip somebody took
 * and cannot retake sitting in IndexedDB forever.
 */
export function normaliseQueueItem(item, entityId) {
  const { blob, width, height, ...rest } = item;
  const pages = item.pages ?? [{ blob, width, height }];
  return { ...rest, entityId: item.entityId ?? entityId, pages };
}

/**
 * The queued items that belong to `entityId`, oldest first, skipping the ones
 * already marked failed.
 *
 * Pure and exported so the entity scoping — the part that keeps one client's
 * slip out of another client's ledger — is testable without IndexedDB or a
 * fake API. `flushQueue` is nothing but this plus the posting loop.
 *
 * Order is `queuedAt`, because `queued()` returns IndexedDB key order, which
 * is UUID-lexicographic and therefore not capture order.
 */
export function itemsForEntity(items, entityId) {
  return items
    .filter((item) => !item.failed)
    .map((item) => normaliseQueueItem(item, entityId))
    .filter((item) => item.entityId === entityId)
    .sort((a, b) => a.queuedAt - b.queuedAt);
}

export async function queued() {
  const db = await open();
  return new Promise((res, rej) => {
    const r = db.transaction(STORE).objectStore(STORE).getAll();
    r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
  });
}

async function drop(clientRef) {
  const db = await open();
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(clientRef);
    tx.oncomplete = res; tx.onerror = () => rej(tx.error);
  });
}

/**
 * A discarded item is a client's deliberate choice, made from what
 * `flushQueue` marked failed below — never something this module decides on
 * its own. Exported so the UI can offer a "Remove" action on a rejected slip.
 */
export const discard = drop;

/** Record that the server will never accept this item, without deleting it. */
async function markFailed(clientRef, message) {
  const db = await open();
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get = store.get(clientRef);
    get.onsuccess = () => {
      const item = get.result;
      if (item) store.put({ ...item, failed: true, failedReason: message });
    };
    tx.oncomplete = res; tx.onerror = () => rej(tx.error);
  });
}

// onerror is not optional. Without it a FileReader failure leaves this
// promise pending forever: flushQueue never returns, send()'s
// `finally { setBusy(false) }` never runs, and the button sits on "Saving…"
// with no error and no way out but a page reload. It was one reader per slip
// before multi-page; it is now up to five under a single Promise.all, any one
// of which can hang the lot.
const toBase64 = (blob) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(String(fr.result).split(',')[1]);
  fr.onerror = () => rej(fr.error);
  fr.readAsDataURL(blob);
});

/**
 * Try to upload everything queued. Safe to call as often as you like: the
 * server deduplicates on clientRef, so a retry after a lost acknowledgement
 * is not a second slip.
 *
 * ONLY items queued for `entityId` are sent — see itemsForEntity. This is not
 * a tidiness measure: a practice user holds `capture.slip` on every client
 * they serve, so the server would accept client A's slip posted to client B
 * without a murmur, allocate it, and with a rule and auto-post enabled put it
 * in client B's journal. Nothing downstream can tell that apart from a real
 * expense. Whatever does not belong to this entity stays queued for the one
 * it does.
 *
 * Processed oldest-first (`queuedAt`) — `queued()` returns IndexedDB key
 * order, which is UUID-lexicographic and therefore not capture order.
 *
 * A failure that will never succeed on retry (a 4xx the server rejects,
 * e.g. an unreadable photo) must not block every good item behind it — that
 * is the same poison-item failure already fixed server-side in the slip
 * worker. Such an item is marked `failed` and skipped, never deleted: it
 * stays visible to the client with its reason, and is only ever removed by
 * an explicit `discard`. A network failure (no `.status` at all) or an
 * auth/permission problem (401/403) stops the whole pass instead, since
 * retrying the rest would fail identically until that is resolved.
 */
export async function flushQueue(api, entityId) {
  const items = itemsForEntity(await queued(), entityId);
  let sent = 0, failed = 0;
  for (const item of items) {
    try {
      await api.post(`/api/entities/${entityId}/slips`, {
        clientRef: item.clientRef,
        // Page order is array order and it is load-bearing: the model is
        // told page 1 comes first, so pages must stay in capture order.
        pages: await Promise.all(item.pages.map(async (p) => ({
          mime: 'image/jpeg',
          width: p.width,
          height: p.height,
          imageBase64: await toBase64(p.blob),
        }))),
      });
      await drop(item.clientRef);
      sent += 1;
    } catch (err) {
      const status = err?.status;
      // No status at all: fetch itself failed — genuinely offline. Stop and
      // leave everything, including this item, for the next attempt.
      if (status === undefined) break;
      // The session or permission needs attention, not this photo. Stop —
      // retrying the rest would fail identically.
      if (status === 401 || status === 403) break;
      // The server will NEVER accept this item. Skip it, marked not deleted,
      // so every good item behind it still gets tried this pass.
      if (status >= 400 && status < 500) {
        await markFailed(item.clientRef, err.message ?? 'rejected by the server');
        failed += 1;
        continue;
      }
      break;   // 5xx — the server is unwell. Try again later.
    }
  }
  return { sent, failed };
}
